import React, { useCallback } from "react";
import {
  Box,
  Table as CKTable,
  Link,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  HStack,
  Badge,
  Card,
  CardBody,
  Divider,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { UsuallyContext } from "@context/usuallyContex";
import { OportunityContext } from "@context/oportunityContex";
import oportunityService from "@services/Oportunity/OportunityService";
import { FaAward } from "react-icons/fa";
import { theme } from "src/styles/theme";
import { useNavigate } from "react-router-dom";
import { FaRegSquareCheck } from "react-icons/fa6";
import { IoAlertCircleOutline, IoTrashOutline } from "react-icons/io5";
import ButtonDropdown from "./ButtonDropdown";
import { TbCalendarPlus } from "react-icons/tb";
import { BiMessageRoundedAdd } from "react-icons/bi";
import { RiLogoutCircleRLine } from "react-icons/ri";
import oportunityServiceInstance from "@services/Oportunity/OportunityService";
import { useMessageErrorToast } from "@hook/useMessageErrorToast";
import { useOnlyMessageToast } from "@hook/useMessageToast";
import Spinner from "@components/common/Spinner";
import { handleDefaultRanking } from "@helpers/defaultValues";
import { cookieKeys, getCookie } from "@helpers/cookies";
import CustomTooltip from "./CustomTooltip";

type TableRow = {
  id: string;
  fase: string;
  status: string;
  nomeDaConta: string;
  nomeDaUnidade: string;
  oportunidadeId: string;
  opportunityIdSalesforce: string;
  idEmpreendimentoSales: string;
  imobiliaria: string;
  imobiliariaId: string;
  ranking: string | null;
  criadorDaOportunidade: string;
  dataUltimaModificacao: string;
  empreendimentoDeInteresse: string;
  diasSemAtualizacao: number;
};

interface IDataTable {
  data: TableRow[];
  onClick: () => void;
}

function TableMyOportunity({ data, onClick }: Readonly<IDataTable>) {
  const navigate = useNavigate();
  const displayMessageError = useMessageErrorToast();
  const displayMessage = useOnlyMessageToast();
  const idCorretor = getCookie(cookieKeys.idCorretor) || "";
  const { setHashSalesSidebar, setHashOportunity, setHashDetailOportunity } =
    React.useContext(UsuallyContext);
  const {
    setLoading,
    setContracts,
    setAccessCreateOp,
    setFaseOp,
    setImobiliariaId,
    setImobiliariaNome,
    setIdEmpreendimentoSales,
    setNomeEmpreendimentoSales,
    setIndexTabDetails,
    setIndexTabActivityComponent,
    setAccessQuote,
  } = React.useContext(OportunityContext);
  const [loadingCreateCall, setLoadingCreateCall] = React.useState(false);

  // ── A ordenação agora é feita pelo backend (ordenarPor / tipoOrdenacao),
  // então aqui só removemos duplicatas e preservamos a ordem recebida da API.
  const filteredRows = React.useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }

    const uniqueMap = new Map<string, TableRow>();

    data.forEach((row) => {
      if (!uniqueMap.has(row.oportunidadeId)) {
        uniqueMap.set(row.oportunidadeId, row);
      }
    });

    return Array.from(uniqueMap.values());
  }, [data]);

  const handleClickId = (
    opportunityIdSalesforce: string,
    oportunidadeId: string,
    oportunidadeName: string,
  ) => {
    localStorage.removeItem("@notificationNavigation");
    localStorage.removeItem("@indexTabDetails");
    localStorage.removeItem("@indexTabActivityComponent");
    localStorage.removeItem("@notificationType");

    setAccessCreateOp({
      oportunidadeId: oportunidadeId,
      nomeDaConta: oportunidadeName,
      oportunityIdSalesforce: opportunityIdSalesforce,
    });
    setLoading(true);

    oportunityService
      .getContracts({ opportunityIdSalesforce: opportunityIdSalesforce })
      .then((c) => {
        setContracts(c);
        setLoading(false);
      });

    navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
      state: {
        oportunidadeId,
      },
    });
    onClick();

    setHashDetailOportunity("DetailOportunity");
    setHashOportunity("DetailOportunityComponent");
    setHashSalesSidebar("DetailOportunityComponent");
    setHashDetailOportunity("DetailOportunity");
    setIndexTabDetails(0);
  };

  const handleAdjustFase = (value: string) => {
    if (value?.toLowerCase() === "aguardando atendimento corretor")
      return "Aguardando atendimento";
    return value;
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return dateString;

    const date = new Date(dateString);
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const getFaseColors = (fase: string) => {
    const lowerFase = fase?.toLowerCase();

    if (
      lowerFase === "cancelada" ||
      lowerFase === "cancelado" ||
      lowerFase?.includes("perdido")
    ) {
      return {
        bg: "#FCF6F6",
        color: "#5E1E31",
        borderColor: "#5E1E31",
      };
    }

    if (lowerFase === "aguardando atendimento corretor") {
      return {
        bg: "#F5F7FA",
        color: theme.text50,
        borderColor: theme.text50,
      };
    }

    return {
      bg: "#F0F4FD",
      color: theme.primary,
      borderColor: theme.primary,
    };
  };

  const handleCreateTasks = useCallback(
    async (dropdownOp: any) => {
      setLoadingCreateCall(true);

      const getDateToDay = () => {
        const today = new Date();
        today.setDate(today.getDate());
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, "0");
        const day = String(today.getDate()).padStart(2, "0");
        return `${day}/${month}/${year}`;
      };

      try {
        const response = await oportunityServiceInstance.createTask({
          idCorretorSalesforce: idCorretor,
          assunto: "Chamada",
          dataDeVencimento: `${getDateToDay()}`,
          relativoA: dropdownOp.opportunityIdSalesforce,
          status: "Concluído",
          tipoDeRegistro: "Call",
          numeroDeTentativas: 1,
          comentarios:
            "Chamada criada automaticamente pelo corretor, através do botão de whatsapp do cliente no Bora Vender.",
        });

        if (response.status >= 400 && response?.status !== 401) {
          setLoadingCreateCall(false);
          displayMessageError({
            message: response?.data?.mensagem
              ? response?.data?.mensagem
              : `Erro ao criar chamada!`,
          });
          console.log("status 400", response);
          return;
        }
        if (response.status === 401) {
          setLoadingCreateCall(false);
          return;
        }

        displayMessage("Chamada registrada com sucesso!");
        setLoadingCreateCall(false);
      } catch (error) {
        setLoadingCreateCall(false);
        console.log("errorHandleCreateTask", error);
      }
    },
    [displayMessage, displayMessageError, idCorretor],
  );

  const handleClickButtonDropdown = React.useCallback(
    async (dropdownOp: any, titleDropdown: string) => {
      try {
        setIndexTabDetails(0);
        setIndexTabActivityComponent(0);

        localStorage.setItem("@indexTabDetails", "0");
        localStorage.setItem("@indexTabActivityComponent", "0");
        localStorage.setItem("@notificationNavigation", "true");
        localStorage.setItem("@notificationType", dropdownOp?.tipoNotificacao);

        setHashOportunity("DetailOportunityComponent");

        setAccessCreateOp((prevState: any) => ({
          ...prevState,
          oportunidadeId: dropdownOp?.oportunidadeId,
          oportunityIdSalesforce:
            dropdownOp?.opportunityIdSalesforce ||
            prevState.oportunityIdSalesforce,
        }));

        if (titleDropdown === "Criar agendamento") {
          setHashDetailOportunity("DetailOportunity");

          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarAgendamento",
              indexTabDetails: 2,
              indexTabActivityComponent: 1,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Abrir Whatsapp") {
          let phone = dropdownOp?.telefonePrincipal?.replace(/\D/g, "");

          if (!phone) {
            console.warn("Telefone inválido ou inexistente");
            return;
          }

          if (!phone.startsWith("55")) {
            phone = "55" + phone;
          }

          const isMobile = /Android|iPhone|iPad|iPod/i.test(
            navigator.userAgent,
          );

          void handleCreateTasks(dropdownOp);

          if (isMobile) {
            window.open(`https://wa.me/${phone}`, "_blank");
          } else {
            window.open(
              `https://web.whatsapp.com/send?phone=${phone}`,
              "_blank",
            );
          }
        } else if (titleDropdown === "Criar tarefa") {
          setHashDetailOportunity("DetailOportunity");

          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarTarefa",
              indexTabDetails: 2,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else if (titleDropdown === "Fechar e perder oportunidade") {
          setHashDetailOportunity("DetailOportunity");

          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "FecharPerder",
              indexTabDetails: 0,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        } else {
          setHashDetailOportunity("DetailOportunity");

          setAccessQuote({
            nomeDaCotacao: dropdownOp?.nomeDaCotacao,
            idCotacaoSalesforce: dropdownOp?.idCotacaoSalesforce,
          });

          navigate(`/oportunidades/minhas-oportunidades/detalhes`, {
            replace: true,
            state: {
              source: "dropdownOp",
              type: "CriarCotacao",
              indexTabDetails: 4,
              indexTabActivityComponent: 0,
              oportunidadeId: dropdownOp?.oportunidadeId,
            },
          });
        }
      } catch (error) {
        console.error(
          "Erro ao processar o redirecionamento do dropdown:",
          error,
        );
      }
    },
    [
      handleCreateTasks,
      navigate,
      setAccessCreateOp,
      setAccessQuote,
      setHashDetailOportunity,
      setHashOportunity,
      setIndexTabActivityComponent,
      setIndexTabDetails,
    ],
  );

  const arrayButtons = React.useMemo(
    () => [
      { icon: <TbCalendarPlus fontSize="18px" />, title: "Criar agendamento" },
      {
        icon: <BiMessageRoundedAdd fontSize="18px" />,
        title: "Abrir Whatsapp",
      },
      { icon: <FaRegSquareCheck fontSize="18px" />, title: "Criar tarefa" },
      { icon: <RiLogoutCircleRLine fontSize="18px" />, title: "Criar cotação" },
      {
        icon: <IoTrashOutline fontSize="18px" />,
        title: "Fechar e perder oportunidade",
      },
    ],
    [],
  );

  const renderDropdown = useCallback(
    (row: any) =>
      arrayButtons
        .filter(
          (button) =>
            !(
              button.title === "Fechar e perder oportunidade" &&
              row.PermitidoQueda === false
            ),
        )
        .map((button) => (
          <Box w="100%" key={button.title}>
            <Flex flexDir={"column"} w="100%" justifyContent={"center"}>
              <Box
                display={"flex"}
                p={"10px"}
                gap={"15px"}
                alignItems={"center"}
                opacity={"0.8"}
                _hover={{ opacity: "1" }}
                cursor="pointer"
                onClick={() => handleClickButtonDropdown(row, button.title)}
              >
                <Box w={"fit-content"} color={theme.text}>
                  {button.icon}
                </Box>
                <Text
                  w={"fit-content"}
                  fontSize={"13px"}
                  fontWeight={"medium"}
                  color={theme.text}
                  _hover={{ color: theme.primary }}
                >
                  {button.title}
                </Text>
              </Box>

              <Divider
                orientation="horizontal"
                borderColor={"#E5E9F0"}
                h={"2px"}
              />
            </Flex>
          </Box>
        )),
    [arrayButtons, handleClickButtonDropdown],
  );

  const formatSla = (diasSemAtualizacao: number) => {
    if (diasSemAtualizacao === null || diasSemAtualizacao === 0) return "";
    else if (diasSemAtualizacao === 1) return "1 dia atrás";
    else return `${diasSemAtualizacao} dias atrás`;
  };

  return (
    <>
      <Box
        display={{ base: "none", md: "block" }}
        overflowX="hidden"
        sx={{
          "&::-webkit-scrollbar": {
            height: "6px",
          },
        }}
      >
        <CKTable flexDirection="column" variant="simple" bg="white">
          <Thead
            h="60px"
            position="sticky"
            top="0"
            zIndex="1"
            bg="white"
            boxShadow="sm"
          >
            <Tr>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  Nome do cliente
                </Text>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  ID da oportunidade
                </Text>
              </Th>
              <Th minW="150px" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  Fase
                </Text>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  Empreendimento de interesse
                </Text>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  Última atualização
                </Text>
              </Th>
              <Th minW="150px" textAlign="center" color={theme.text}>
                <Text fontSize={11} w={"fit-content"}>
                  Criador da oportunidade
                </Text>
              </Th>

              <Th
                minW="60px"
                position="sticky"
                right="0"
                zIndex={10}
                bg="white"
                textAlign="center"
              >
                <Text alignItems={"center"} fontSize={11} w={"fit-content"}>
                  Ações{" "}
                </Text>
              </Th>
            </Tr>
          </Thead>

          <Tbody>
            {filteredRows.map((row: any) => {
              const faseColors = getFaseColors(row.fase);

              return (
                <Link
                  key={row?.oportunidadeId}
                  href="/oportunidades/minhas-oportunidades/detalhes"
                  onClick={(e: any) => {
                    if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                      e.preventDefault();

                      localStorage.setItem(
                        "@oportunidade_nova_aba",
                        JSON.stringify({
                          oportunidadeId: row?.oportunidadeId,
                          nomeDaConta: row?.nomeDaConta,
                          opportunityIdSalesforce: row?.opportunityIdSalesforce,
                          fase: row?.fase,
                          empreendimentoDeInteresse:
                            row?.empreendimentoDeInteresse,
                          idEmpreendimentoSales: row?.idEmpreendimentoSales,
                          imobiliaria: row?.imobiliaria,
                          imobiliariaId: row?.imobiliariaId,
                        }),
                      );

                      handleClickId(
                        row?.opportunityIdSalesforce,
                        row?.oportunidadeId,
                        row?.nomeDaConta,
                      );
                      setFaseOp(
                        row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                          row?.fase?.toLocaleLowerCase() ===
                            "fechado e perdido",
                      );
                      setNomeEmpreendimentoSales(
                        row?.empreendimentoDeInteresse,
                      );
                      setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                      setImobiliariaNome(row?.imobiliaria);
                      setImobiliariaId(row?.imobiliariaId);
                    }
                  }}
                  display={"contents"}
                >
                  <Tr
                    key={row?.oportunidadeId}
                    _hover={{ bg: "#e2e5f0" }}
                    borderLeft={
                      row.origemDigital ? `3px solid ${theme.error50}` : "none"
                    }
                    cursor={"pointer"}
                    onClick={() => {
                      handleClickId(
                        row?.opportunityIdSalesforce,
                        row?.oportunidadeId,
                        row?.nomeDaConta,
                      );
                      setFaseOp(
                        row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                          row?.fase?.toLocaleLowerCase() ===
                            "fechado e perdido",
                      );
                      setNomeEmpreendimentoSales(
                        row?.empreendimentoDeInteresse,
                      );
                      setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                      setImobiliariaNome(row?.imobiliaria);
                      setImobiliariaId(row?.imobiliariaId);
                    }}
                    onMouseDown={() => {
                      localStorage.setItem(
                        "@oportunidade_nova_aba",
                        JSON.stringify({
                          oportunidadeId: row?.oportunidadeId,
                          nomeDaConta: row?.nomeDaConta,
                          opportunityIdSalesforce: row?.opportunityIdSalesforce,
                          fase: row?.fase,
                          empreendimentoDeInteresse:
                            row?.empreendimentoDeInteresse,
                          idEmpreendimentoSales: row?.idEmpreendimentoSales,
                          imobiliaria: row?.imobiliaria,
                          imobiliariaId: row?.imobiliariaId,
                        }),
                      );
                    }}
                  >
                    {/* Nome */}
                    <Td minW="150px" color={theme.text} verticalAlign="middle">
                      <HStack spacing={2} w={"fit-content"}>
                        <Text
                          fontSize="13px"
                          fontWeight="500"
                          maxW={{
                            base: "140px",
                            md: "130px",
                            "2xl": "fit-content",
                          }}
                        >
                          {row.nomeDaConta?.toUpperCase()}
                        </Text>
                        {row?.ranking !== null && (
                          <CustomTooltip content={row?.ranking}>
                            <Box
                              display={"flex"}
                              w={"fit-content"}
                              alignItems={"center"}
                              justifyContent={"center"}
                            >
                              <FaAward
                                color={handleDefaultRanking(row?.ranking)}
                                fontSize={"16px"}
                              />
                            </Box>
                          </CustomTooltip>
                        )}
                      </HStack>
                    </Td>

                    {/* ID */}
                    <Td
                      minW="150px"
                      color={theme.text50}
                      verticalAlign="middle"
                    >
                      <Text fontSize="13px" color={theme.text50}>
                        {row.oportunidadeId}
                      </Text>
                    </Td>

                    {/* Fase */}
                    <Td minW="150px" verticalAlign="middle">
                      <Flex justify="center" w="180px" p={"4px"}>
                        <Badge
                          bg={faseColors.bg}
                          color={faseColors.color}
                          border="1px solid"
                          borderColor={faseColors.borderColor}
                          borderRadius="16px"
                          py={2}
                          px={4}
                          fontSize="11px"
                          w="fit-content"
                          minW="200px"
                          whiteSpace="normal"
                          wordBreak="break-word"
                          textAlign="center"
                          lineHeight="1.3"
                        >
                          {handleAdjustFase(row.fase)}
                        </Badge>
                      </Flex>
                    </Td>

                    {/* Empreendimento */}
                    <Td minW="150px" color={theme.text} verticalAlign="middle">
                      <Text fontSize="13px" maxW="200px">
                        {row.empreendimentoDeInteresse}
                      </Text>
                    </Td>

                    {/* Data */}
                    <Td minW="150px" color={theme.text} verticalAlign="middle">
                      <Flex
                        display={"flex"}
                        align={"center"}
                        justify={"center"}
                        textAlign={"center"}
                        gap={"2px"}
                      >
                        {row.diasSemAtualizacao > 1 && (
                          <Box w={"fit-content"}>
                            <IoAlertCircleOutline color={theme.error} />
                          </Box>
                        )}

                        <Text
                          fontSize="13px"
                          w={"fit-content"}
                          color={
                            row.diasSemAtualizacao > 1
                              ? theme.error
                              : theme.text
                          }
                          fontWeight={"500"}
                        >
                          {formatSla(row.diasSemAtualizacao)}
                        </Text>
                      </Flex>
                    </Td>

                    {/* Origem */}
                    <Td
                      minW="150px"
                      color={theme.text}
                      verticalAlign="middle"
                      textAlign={"center"}
                    >
                      <Text fontSize="13px">{row.criadorDaOportunidade}</Text>
                    </Td>

                    {/* AÇÕES */}
                    <Td
                      minW="60px"
                      position="sticky"
                      right="0"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}
                      verticalAlign="middle"
                    >
                      {loadingCreateCall ? (
                        <Box h={"15px"}>
                          <Spinner size="sm" />
                        </Box>
                      ) : (
                        <ButtonDropdown popoverContent={renderDropdown(row)} />
                      )}
                    </Td>
                  </Tr>
                </Link>
              );
            })}
          </Tbody>
        </CKTable>
      </Box>

      {/* VISUALIZAÇÃO MOBILE EM FORMATO DE CARD - CHAKRA UI */}
      <VStack
        spacing={4}
        display={{ base: "flex", md: "none" }}
        w="full"
        px={4}
        py={2}
      >
        {filteredRows.map((row: any) => {
          const faseColors = getFaseColors(row.fase);

          return (
            <Card
              key={row.oportunidadeId}
              w="full"
              shadow="md"
              borderRadius="12px"
              border="1px solid"
              borderColor="gray.200"
              cursor="pointer"
              transition="all 0.2s"
              _hover={{
                shadow: "lg",
                borderColor: theme.primary,
                transform: "translateY(-2px)",
              }}
              _active={{
                transform: "translateY(0px)",
              }}
              onClick={(e: any) => {
                if (!e.ctrlKey && !e.metaKey && e.button === 0) {
                  e.preventDefault();

                  localStorage.setItem(
                    "@oportunidade_nova_aba",
                    JSON.stringify({
                      oportunidadeId: row?.oportunidadeId,
                      nomeDaConta: row?.nomeDaConta,
                      opportunityIdSalesforce: row?.opportunityIdSalesforce,
                      fase: row?.fase,
                      empreendimentoDeInteresse: row?.empreendimentoDeInteresse,
                      idEmpreendimentoSales: row?.idEmpreendimentoSales,
                      imobiliaria: row?.imobiliaria,
                      imobiliariaId: row?.imobiliariaId,
                    }),
                  );

                  handleClickId(
                    row?.opportunityIdSalesforce,
                    row?.oportunidadeId,
                    row?.nomeDaConta,
                  );
                  setFaseOp(
                    row?.fase?.toLocaleLowerCase() === "fechado e ganho" ||
                      row?.fase?.toLocaleLowerCase() === "fechado e perdido",
                  );
                  setNomeEmpreendimentoSales(row?.empreendimentoDeInteresse);
                  setIdEmpreendimentoSales(row?.idEmpreendimentoSales);
                  setImobiliariaNome(row?.imobiliaria);
                  setImobiliariaId(row?.imobiliariaId);
                }
              }}
            >
              <CardBody p={4}>
                <Flex justify="space-between" align="center" mb={3}>
                  <HStack spacing={2} flex={1} justify="space-between">
                    <Text
                      w={"fit-content"}
                      fontSize="md"
                      fontWeight="medium"
                      color={theme.text}
                      flex={1}
                    >
                      {row.nomeDaConta?.toUpperCase()}
                    </Text>

                    <Box
                      w={"fit-content"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <ButtonDropdown popoverContent={renderDropdown(row)} />
                    </Box>
                  </HStack>
                  {row?.ranking && (
                    <CustomTooltip content={row.ranking}>
                      <Icon
                        as={FaAward}
                        color={handleDefaultRanking(row.ranking)}
                        boxSize={5}
                      />
                    </CustomTooltip>
                  )}
                </Flex>

                <Flex justify="center" mb={4}>
                  <Badge
                    bg={faseColors.bg}
                    color={faseColors.color}
                    borderColor={faseColors.borderColor}
                    border="1px solid"
                    borderRadius="full"
                    px={3}
                    py={1}
                    fontSize="xs"
                    fontWeight="semibold"
                    textAlign="center"
                    minW="120px"
                  >
                    {handleAdjustFase(row.fase)}
                  </Badge>
                </Flex>

                <VStack>
                  <HStack spacing={2}>
                    <Text
                      fontSize="xs"
                      color={theme.text50}
                      fontWeight="medium"
                      w={"fit-content"}
                    >
                      ID da Oportunidade:
                    </Text>
                    <Text
                      fontSize="xs"
                      color={theme.primary}
                      fontWeight="medium"
                      w={"fit-content"}
                    >
                      {row.oportunidadeId}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack spacing={2} align="flex-start">
                    <HStack spacing={1} align="flex-start" flex={1}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Empreendimento de interesse:
                      </Text>
                      <Text
                        fontSize="sm"
                        color={theme.text}
                        lineHeight="short"
                        w={"fit-content"}
                      >
                        {row.empreendimentoDeInteresse}
                      </Text>
                    </HStack>
                  </HStack>

                  <Divider />

                  <VStack spacing={2} align="stretch">
                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Data de ultima modificação:
                      </Text>
                      <Text fontSize="xs" color={theme.text} w={"fit-content"}>
                        {formatDate(row.dataUltimaModificacao)}
                      </Text>
                    </HStack>

                    <HStack spacing={2}>
                      <Text
                        fontSize="xs"
                        color={theme.text50}
                        fontWeight="medium"
                        w={"fit-content"}
                      >
                        Criador da oportunidade:
                      </Text>
                      <Text fontSize="xs" color={theme.text} w={"fit-content"}>
                        {row.criadorDaOportunidade}
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>
          );
        })}
      </VStack>
    </>
  );
}

export default React.memo(TableMyOportunity);
